import { useState } from 'preact/hooks';
import type { PageId } from '../components/Nav.tsx';
import { IconWrap } from '../components/Icon.tsx';
import { TaskModal } from '../features/misc/SimpleModals.tsx';
import { addDays, toLocalISO } from '../lib/dates.ts';
import { live, useStore } from '../store/store.ts';
import { openModal } from '../ui/ui-store.ts';

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const;

function monthCells(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1, 12);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = addDays(first, -mondayOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

export function TasksPage({ onNavigate }: { onNavigate: (page: PageId) => void }) {
  const tasks = live(useStore((s) => s.tasks));
  const toggleTask = useStore((s) => s.toggleTask);
  const deleteTask = useStore((s) => s.deleteTask);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 12);
  });

  const cells = monthCells(month);
  const todayIso = toLocalISO(new Date());
  const monthLabel = month.toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
  const datedTasks = (iso: string) => tasks.filter((task) => task.due === iso);

  function moveMonth(offset: number) {
    setMonth(new Date(month.getFullYear(), month.getMonth() + offset, 1, 12));
  }

  function addForDate(due = '') {
    openModal('Nouvelle tâche', <TaskModal initialDue={due} />);
  }

  // Edit and duplicate live in the dialog, as they do for a lesson: the
  // calendar cells are too small to carry more than one button per task.
  function openTask(id: string) {
    openModal('Modifier la tâche', <TaskModal taskId={id} />);
  }

  return (
    <section class="page tasksPage">
      <div class="sectionTitle">
        <div>
          <button class="btn soft tasksBack" onClick={() => onNavigate('home')}>
            ← Accueil
          </button>
          <h1>Mes tâches</h1>
          <p class="muted">Note ici les choses à ne pas oublier et place-les à la bonne date.</p>
        </div>
        <button class="btn primary" onClick={() => addForDate()}>
          + Ajouter une tâche
        </button>
      </div>

      <div class="taskCalendarToolbar">
        <button class="btn" onClick={() => moveMonth(-1)} aria-label="Mois précédent">←</button>
        <h2>{monthLabel}</h2>
        <button class="btn" onClick={() => moveMonth(1)} aria-label="Mois suivant">→</button>
      </div>

      <div class="card taskCalendar">
        {DAY_NAMES.map((name) => <div key={name} class="taskCalendarDayName">{name}</div>)}
        {cells.map((date) => {
          const iso = toLocalISO(date);
          const dayTasks = datedTasks(iso);
          const outside = date.getMonth() !== month.getMonth();
          return (
            <div key={iso} class={`taskCalendarCell${outside ? ' outside' : ''}${iso === todayIso ? ' today' : ''}`}>
              <button class="taskCalendarDate" onClick={() => addForDate(iso)} aria-label={`Ajouter une tâche le ${date.toLocaleDateString('fr-BE')}`}>
                {date.getDate()}
              </button>
              {dayTasks.map((task) => (
                <div key={task.id} class={`calendarTask${task.done ? ' done' : ''}`}>
                  <input
                    type="checkbox"
                    checked={task.done}
                    aria-label={`Marquer « ${task.text} » comme faite`}
                    onChange={() => toggleTask(task.id)}
                  />
                  <button
                    class="taskOpen"
                    title="Modifier ou dupliquer cette tâche"
                    onClick={() => openTask(task.id)}
                  >
                    <span>{task.text}</span>
                  </button>
                  <button
                    class="taskDelete"
                    aria-label={`Supprimer la tâche « ${task.text} »`}
                    title="Supprimer cette tâche"
                    onClick={() => deleteTask(task.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {tasks.some((task) => !/^\d{4}-\d{2}-\d{2}$/.test(task.due)) && (
        <div class="card unscheduledTasks">
          <div class="cardHead">
            <IconWrap name="check" background="#eaf6ee" />
            <h3>Sans date précise</h3>
          </div>
          {tasks.filter((task) => !/^\d{4}-\d{2}-\d{2}$/.test(task.due)).map((task) => (
            <div key={task.id} class={`task${task.done ? ' done' : ''}`}>
              <input
                type="checkbox"
                checked={task.done}
                aria-label={`Marquer « ${task.text} » comme faite`}
                onChange={() => toggleTask(task.id)}
              />
              <button
                class="taskOpen"
                title="Modifier ou dupliquer cette tâche"
                onClick={() => openTask(task.id)}
              >
                <span>{task.text}</span>
              </button>
              <span class="small muted">{task.due}</span>
              <button
                class="taskDelete"
                aria-label={`Supprimer la tâche « ${task.text} »`}
                title="Supprimer cette tâche"
                onClick={() => deleteTask(task.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
